import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { CustomField } from '../../types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { LoadingSpinner } from '@shared/components';

interface DynamicFieldsDisplayProps {
  customFields: CustomField[];
  dynamicFieldList: any[];
  isLoading?: boolean;
  InfoRow: React.FC<{ label: string; value: React.ReactNode; tooltip?: string }>;
}

const DynamicFieldsDisplay: React.FC<DynamicFieldsDisplayProps> = ({
  customFields,
  dynamicFieldList,
  isLoading,
  InfoRow,
}) => {
  if (isLoading) {
    return <LoadingSpinner message="Loading dynamic fields..." />;
  }

  if (!customFields || customFields.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No dynamic fields configured for this offer
      </Typography>
    );
  }

  // Create a map of field definitions for quick lookup
  const fieldDefinitionsMap = new Map(dynamicFieldList?.map((field) => [field.id || field.fieldId, field]) || []);

  const renderFieldValue = (field: CustomField, fieldDef: any) => {
    if (!field.values) {
      return <Typography variant="body2">-</Typography>;
    }

    // Normalize values to array of strings
    let valuesArray: string[] = [];

    if (Array.isArray(field.values)) {
      // Handle array of objects with value property or array of strings
      valuesArray = field.values.map((v) =>
        typeof v === 'object' && v !== null && 'value' in v ? v.value : String(v),
      );
    } else {
      valuesArray = [String(field.values)];
    }

    if (valuesArray.length === 0) {
      return <Typography variant="body2">-</Typography>;
    }

    const value = valuesArray[0];

    // Handle boolean fields
    if (fieldDef?.type === 'boolean' || value === 'true' || value === 'false') {
      const isTrue = value === 'true';
      return (
        <Chip
          icon={isTrue ? <CheckCircleIcon /> : <CancelIcon />}
          label={isTrue ? 'Yes' : 'No'}
          size="small"
          color={isTrue ? 'success' : 'default'}
          variant="outlined"
        />
      );
    }

    // Handle array/multiple values
    if (valuesArray.length > 1) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
          {valuesArray.map((v, idx) => (
            <Chip key={idx} label={v} size="small" variant="outlined" />
          ))}
        </Box>
      );
    }

    // Handle single value
    return (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    );
  };

  return (
    <>
      {customFields.map((field) => {
        const fieldDef = fieldDefinitionsMap.get(field.fieldId);
        const fieldLabel = fieldDef?.label || fieldDef?.key || `Field ${field.fieldId}`;
        const tooltip = fieldDef?.description || fieldDef?.tooltip;

        return (
          <InfoRow key={field.fieldId} label={fieldLabel} value={renderFieldValue(field, fieldDef)} tooltip={tooltip} />
        );
      })}
    </>
  );
};

export default DynamicFieldsDisplay;
